import { masterBreakPoint, maxWidth600 } from "themes/default/breakpoints"

export const styles = {
  dialog: (theme) => ({
    backgroundColor: theme.palette.background.default,
    [maxWidth600]: {
      m: 1,
      width: "calc(100% - 32px)",
    },
  }),
  closeButton: (theme) => ({
    position: "absolute",
    right: 16,
    top: 16,
    border: "none",
    cursor: "pointer",
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: theme.palette.background.secondary,
    color: theme.palette.text.lighter1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: theme.palette.hover.main,
      color: theme.palette.common.black,
    },
    zIndex: 10,
  }),
  container: (theme) => ({
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.default,
    p: 4,
    position: "relative",
  }),
  headerRow: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: "center",
    justifyContent: "space-between",
    gap: 3,
    width: "100%",
    [masterBreakPoint]: {
      gap: 1,
    },
  },
  headerLeft: {
    display: "flex",
    flexDirection: "column",
    alignItems: { xs: "center", md: "flex-start" },
    textAlign: { xs: "center", md: "left" },
    flex: 1,
  },
  ticketWrapper: {
    transform: "rotate(-8deg)",
    display: "inline-block",
  },
  titleLine1: (theme) => ({
    fontWeight: "bold",
    color: theme.palette.licenses.surface,
    fontSize: "3rem",
    [masterBreakPoint]: {
      fontSize: "2rem",
    },
  }),
  titleLine2: (theme) => ({
    fontWeight: "bold",
    color: theme.palette.primary.main,
    fontSize: "3rem",
    [masterBreakPoint]: {
      fontSize: "2rem",
    },
  }),
  subtitle: (theme) => ({
    color: theme.palette.licenses.surface,
    fontSize: "1.1rem",
  }),
  headerRight: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  bgCircle: (theme) => ({
    position: "absolute",
    minHeight: 150,
    minWidth: 150,
    width: "20vh",
    height: "20vh",
    borderRadius: "50%",
    backgroundColor: theme.palette.licenses.surface3,
    zIndex: 0,
    [maxWidth600]: {
      minHeight: 100,
      minWidth: 100,
      width: "15vh",
      height: "15vh",
    },
  }),
  mascot: {
    minHeight: 150,
    height: "20vh",
    objectFit: "contain",
    position: "relative",
    zIndex: 2,
    [maxWidth600]: {
      minHeight: 100,
      height: "15vh",
    },
  },
  leftIcon: {
    width: "4rem",
    height: "4rem",
    objectFit: "contain",
    [masterBreakPoint]: {
      width: "2rem",
      height: "2rem",
    },
  },
  floatingAccent: {
    position: "absolute",
    zIndex: 3,
    animation: "pulse 3s ease-in-out infinite",
    "@keyframes pulse": {
      "0%, 100%": { transform: "scale(1)" },
      "50%": { transform: "scale(1.15)" },
    },
  },
  sectionDividerRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    my: 2,
    gap: 1,
    [masterBreakPoint]: {
      my: 1,
    },
  },
  dividerLine: (theme) => ({
    height: "1px",
    backgroundColor: theme.palette.licenses.text,
    flex: 1,
  }),
  sectionLabel: (theme) => ({
    color: theme.palette.licenses.surface,
    fontWeight: 600,
    fontSize: "0.9rem",
    textTransform: "none",
  }),
  featuresGrid: {
    width: "100%",
    mb: 4,
    [masterBreakPoint]: {
      mb: 2,
    },
  },
  featureCol: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: { xs: "center", md: "flex-start" },
    textAlign: { xs: "center", md: "left" },
    px: { xs: 1, md: 2 },
  },
  featureIconContainer: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    mb: 2,
    flexShrink: 0,
  },
  featureTitle: {
    fontWeight: 700,
    color: "#0B1E36",
    fontSize: "1.05rem",
    mb: 1,
  },
  featureDesc: {
    color: "#6B7280",
    fontSize: "0.875rem",
    lineHeight: 1.4,
  },
  verticalBorder: {
    position: "absolute",
    right: 0,
    top: "10%",
    height: "80%",
    width: "1px",
    backgroundColor: "#E5E7EB",
    display: { xs: "none", md: "block" },
  },
  teacherBanner: (theme) => ({
    width: "100%",
    backgroundColor: theme.palette.licenses.surface7,
    borderRadius: 2,
    p: 2,
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: "center",
    gap: 2,
    mb: 3,
    [masterBreakPoint]: {
      gap: 1,
      mb: 2,
      py: 1,
    },
  }),
  bannerIconWrapper: (theme) => ({
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: theme.palette.licenses.surface7,
    border: `1px solid ${theme.palette.licenses.surface8}`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  }),
  bannerIcon: (theme) => ({
    color: theme.palette.licenses.surface8,
    width: "24px",
    height: "24px",
  }),
  bannerTextContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: { xs: "center", sm: "flex-start" },
    textAlign: { xs: "center", sm: "left" },
  },
  bannerTitle: (theme) => ({
    color: theme.palette.licenses.surface,
    fontSize: "1rem",
    fontWeight: 600,
    mb: 0.25,
  }),
  bannerDesc: (theme) => ({
    color: theme.palette.licenses.surface,
    fontSize: "0.875rem",
  }),
  actionsRow: {
    width: "100%",
    mb: 3,
    [masterBreakPoint]: {
      mb: 2,
    },
  },
  buttonCancel: {
    flex: 1,
    py: 1.5,
    fontWeight: 700,
    textTransform: "none",
    borderRadius: 2.5,
  },
  buttonConfirm: {
    flex: 1,
    py: 1.5,
    fontWeight: 700,
    textTransform: "none",
    borderRadius: 2.5,
  },
  footerNote: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 0.75,
    width: "100%",
  },
  footerText: (theme) => ({
    color: theme.palette.licenses.surface,
    fontSize: "1rem",
    fontWeight: 500,
  }),
  footerIcon: (theme) => ({
    color: theme.palette.licenses.surface,
    fontSize: "1.2rem",
  }),
  confirmIcon: (theme) => ({
    width: 20,
    height: 20,
  }),
}
